import { Link, useNavigate } from "react-router-dom";
import { Star, MapPin, Video, BadgeCheck, Building2, Stethoscope, Microscope, Hospital } from "lucide-react";
import { inr } from "@/lib/api";
import { DemoBadge } from "./Page";
import { AnimatedCard } from "@/motion";

export const TYPE_META = {
  doctor: { label: "Doctor", Icon: Stethoscope, color: "bg-sky-50 text-sky-700" },
  hospital: { label: "Hospital", Icon: Hospital, color: "bg-indigo-50 text-indigo-700" },
  clinic: { label: "Clinic", Icon: Building2, color: "bg-teal-50 text-teal-700" },
  diagnostic: { label: "Diagnostics", Icon: Microscope, color: "bg-rose-50 text-rose-700" },
};

export function ProviderCard({ p, compact, onHover }) {
  const nav = useNavigate();
  const meta = TYPE_META[p.type] || TYPE_META.clinic;
  return (
    <AnimatedCard data-testid={`provider-card-${p.id}`} cursor="VIEW →" onMouseEnter={() => onHover?.(p.id)} onMouseLeave={() => onHover?.(null)}
      className={`relative flex ${compact ? "flex-row" : "flex-col"} bg-white rounded-2xl border border-slate-200 overflow-hidden transition-shadow duration-300 hover:shadow-[0_24px_48px_-20px_rgba(6,21,45,.35)]`}>
      <Link to={`/provider/${p.id}`} className={`${compact ? "w-28 sm:w-36 shrink-0" : "aspect-[16/10]"} relative overflow-hidden bg-slate-100`}>
        <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className={`absolute top-2 left-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.color}`}><meta.Icon size={11} /> {meta.label}</span>
      </Link>
      <div className="flex-1 p-4 flex flex-col gap-2 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link to={`/provider/${p.id}`} className="font-display font-bold text-[#06152D] leading-tight hover:text-[#0284C7] transition-colors line-clamp-1 flex items-center gap-1" data-testid="provider-card-name">
              {p.name} {p.verified && <BadgeCheck size={15} className="text-[#0284C7] shrink-0" />}
            </Link>
            <div className="text-xs text-[#475569] mt-0.5 line-clamp-1">{p.type === "doctor" ? `${p.specialty} · ${p.experience_years} yrs exp` : (p.specialties || []).slice(0, 3).join(" · ")}</div>
          </div>
          <div className="flex items-center gap-1 rounded-lg bg-[#06152D] text-white px-2 py-1 text-xs font-bold shrink-0"><Star size={11} className="fill-amber-400 text-amber-400" /> {p.rating}</div>
        </div>
        <div className="flex items-center gap-1 text-xs text-[#475569]"><MapPin size={12} /> <span className="line-clamp-1">{p.area}, {p.city}</span>{p.distance_km != null && <span className="ml-1 text-[#0284C7] font-semibold">· {p.distance_km} km</span>}</div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <div className="flex items-center gap-2 text-xs">
            {p.type === "doctor" ? <span className="font-bold text-[#06152D]">{inr(p.fee)} <span className="font-normal text-slate-500">/ visit</span></span>
              : <span className="text-slate-600">{p.emergency_24x7 ? "24×7 Emergency" : p.timings}</span>}
            {p.video_consult && <span className="inline-flex items-center gap-1 text-teal-700"><Video size={12} /> Video</span>}
            {p.is_demo && <DemoBadge />}
          </div>
          <button data-testid="provider-card-book-now-button" data-cursor="BOOK" onClick={() => nav(`/provider/${p.id}?book=1`)}
            className="rounded-full bg-[#06152D] text-white text-xs font-semibold px-3.5 py-1.5 hover:bg-[#0284C7] transition-colors">Book Now</button>
        </div>
      </div>
    </AnimatedCard>
  );
}
